/**
 * SKOP Whale Wallet Tracker
 *
 * Track whale wallets and get notified of their activity.
 */

// ============================================================================
// Types
// ============================================================================

export interface TrackedWallet {
  address: string;
  label: string;
  notes?: string;
  addedAt: number;
  lastActivity?: number;
  totalTrades?: number;
  profitLoss?: number;
  category: WalletCategory;
}

export type WalletCategory = 'whale' | 'smart_money' | 'dev' | 'influencer' | 'custom';

export interface WalletActivity {
  id: string;
  walletAddress: string;
  type: 'buy' | 'sell' | 'transfer';
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  amount: number;
  amountUsd?: number;
  pricePerToken?: number;
  timestamp: number;
  signature: string;
}

export interface WhaleAlert {
  id: string;
  walletAddress: string;
  walletLabel: string;
  activity: WalletActivity;
  seen: boolean;
  notified: boolean;
  createdAt: number;
}

// ============================================================================
// Storage
// ============================================================================

const TRACKED_WALLETS_KEY = 'skop_tracked_wallets';
const WHALE_ALERTS_KEY = 'skop_whale_alerts';
const WHALE_ACTIVITY_KEY = 'skop_whale_activity';

/**
 * Get tracked wallets for a user
 */
export function getTrackedWallets(userWallet: string): TrackedWallet[] {
  try {
    const stored = localStorage.getItem(`${TRACKED_WALLETS_KEY}_${userWallet}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save tracked wallets
 */
function saveTrackedWallets(userWallet: string, wallets: TrackedWallet[]): void {
  localStorage.setItem(
    `${TRACKED_WALLETS_KEY}_${userWallet}`,
    JSON.stringify(wallets)
  );
}

/**
 * Add a wallet to track
 */
export function trackWallet(
  userWallet: string,
  wallet: {
    address: string;
    label: string;
    notes?: string;
    category: WalletCategory;
  }
): TrackedWallet {
  const wallets = getTrackedWallets(userWallet);

  // Check if already tracking
  const existing = wallets.find(w => w.address === wallet.address);
  if (existing) {
    return existing;
  }

  const tracked: TrackedWallet = {
    ...wallet,
    addedAt: Date.now(),
  };

  wallets.unshift(tracked);
  saveTrackedWallets(userWallet, wallets);

  return tracked;
}

/**
 * Remove a tracked wallet
 */
export function untrackWallet(userWallet: string, walletAddress: string): void {
  const wallets = getTrackedWallets(userWallet);
  const filtered = wallets.filter(w => w.address !== walletAddress);
  saveTrackedWallets(userWallet, filtered);
}

/**
 * Update a tracked wallet
 */
export function updateTrackedWallet(
  userWallet: string,
  walletAddress: string,
  updates: Partial<Omit<TrackedWallet, 'address' | 'addedAt'>>
): void {
  const wallets = getTrackedWallets(userWallet);
  const idx = wallets.findIndex(w => w.address === walletAddress);

  if (idx !== -1) {
    wallets[idx] = { ...wallets[idx], ...updates };
    saveTrackedWallets(userWallet, wallets);
  }
}

/**
 * Check if a wallet is being tracked
 */
export function isWalletTracked(userWallet: string, walletAddress: string): boolean {
  return getTrackedWallets(userWallet).some(w => w.address === walletAddress);
}

// ============================================================================
// Whale Alerts
// ============================================================================

/**
 * Get whale alerts
 */
export function getWhaleAlerts(userWallet: string): WhaleAlert[] {
  try {
    const stored = localStorage.getItem(`${WHALE_ALERTS_KEY}_${userWallet}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save whale alerts
 */
function saveWhaleAlerts(userWallet: string, alerts: WhaleAlert[]): void {
  localStorage.setItem(
    `${WHALE_ALERTS_KEY}_${userWallet}`,
    JSON.stringify(alerts)
  );
}

/**
 * Add a whale alert
 */
export function addWhaleAlert(
  userWallet: string,
  walletAddress: string,
  walletLabel: string,
  activity: WalletActivity
): WhaleAlert {
  const alerts = getWhaleAlerts(userWallet);

  const alert: WhaleAlert = {
    id: `whale_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    walletAddress,
    walletLabel,
    activity,
    seen: false,
    notified: false,
    createdAt: Date.now(),
  };

  alerts.unshift(alert);

  // Keep last 100 alerts
  const trimmed = alerts.slice(0, 100);
  saveWhaleAlerts(userWallet, trimmed);

  return alert;
}

/**
 * Mark alerts as seen
 */
export function markAlertsSeen(userWallet: string, alertIds: string[]): void {
  const alerts = getWhaleAlerts(userWallet);
  const updated = alerts.map(a =>
    alertIds.includes(a.id) ? { ...a, seen: true } : a
  );
  saveWhaleAlerts(userWallet, updated);
}

/**
 * Get unseen alert count
 */
export function getUnseenAlertCount(userWallet: string): number {
  return getWhaleAlerts(userWallet).filter(a => !a.seen).length;
}

/**
 * Clear all alerts
 */
export function clearWhaleAlerts(userWallet: string): void {
  saveWhaleAlerts(userWallet, []);
}

// ============================================================================
// Activity History
// ============================================================================

/**
 * Get recent activity for a tracked wallet
 */
export function getWalletActivity(walletAddress: string): WalletActivity[] {
  try {
    const stored = localStorage.getItem(`${WHALE_ACTIVITY_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save wallet activity
 */
export function saveWalletActivity(walletAddress: string, activity: WalletActivity[]): void {
  // Keep last 50 activities
  const trimmed = activity.slice(0, 50);
  localStorage.setItem(
    `${WHALE_ACTIVITY_KEY}_${walletAddress}`,
    JSON.stringify(trimmed)
  );
}

// ============================================================================
// Popular Whales (Curated List)
// ============================================================================

export interface PopularWhale {
  address: string;
  label: string;
  description: string;
  category: WalletCategory;
  verified: boolean;
  followers?: number;
}

// This would be fetched from an API in production
export const POPULAR_WHALES: PopularWhale[] = [
  {
    address: '5tzFkiKscXHK5ZXCGbXZxdw7gTjjD1mBwuoFbhUvuAi9',
    label: 'GCR',
    description: 'Legendary crypto trader',
    category: 'whale',
    verified: true,
    followers: 50000,
  },
  {
    address: 'HNtB4Fn2jB7EvM7vfpXtKzpCoVVCGsCvy7bAJH9M4LK3',
    label: 'Ansem',
    description: 'Memecoin degen',
    category: 'influencer',
    verified: true,
    followers: 200000,
  },
  {
    address: '2Z3eRRX5yL1jjQsMF3pCVX7JCwqfWNDFxuAz1bGnYJHs',
    label: 'Smart Money 1',
    description: 'High win rate trader',
    category: 'smart_money',
    verified: false,
    followers: 5000,
  },
];

/**
 * Get popular whales to follow
 */
export function getPopularWhales(): PopularWhale[] {
  return POPULAR_WHALES;
}

// ============================================================================
// Wallet Analysis
// ============================================================================

export interface WalletStats {
  totalTrades: number;
  winRate: number;
  avgProfitPercent: number;
  totalProfitUsd: number;
  bestTrade: {
    tokenSymbol: string;
    profitPercent: number;
  } | null;
  worstTrade: {
    tokenSymbol: string;
    lossPercent: number;
  } | null;
  favoriteTokens: string[];
  lastActive: number;
}

/**
 * Analyze a wallet's trading history
 * In production, this would fetch real on-chain data
 */
export async function analyzeWallet(_walletAddress: string): Promise<WalletStats | null> {
  // This is a mock implementation
  // In production, would use Helius/Shyft to fetch real transaction history

  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    // Return mock stats
    return {
      totalTrades: Math.floor(Math.random() * 500) + 50,
      winRate: Math.random() * 40 + 40, // 40-80%
      avgProfitPercent: Math.random() * 100 - 20, // -20% to 80%
      totalProfitUsd: Math.random() * 100000 - 10000,
      bestTrade: {
        tokenSymbol: 'WIF',
        profitPercent: Math.random() * 1000 + 100,
      },
      worstTrade: {
        tokenSymbol: 'RUGGED',
        lossPercent: -(Math.random() * 90 + 10),
      },
      favoriteTokens: ['WIF', 'BONK', 'POPCAT'],
      lastActive: Date.now() - Math.random() * 24 * 60 * 60 * 1000,
    };
  } catch {
    return null;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format category for display
 */
export function formatCategory(category: WalletCategory): { label: string; color: string } {
  switch (category) {
    case 'whale':
      return { label: 'Whale', color: '#00f0ff' };
    case 'smart_money':
      return { label: 'Smart Money', color: '#00ff88' };
    case 'dev':
      return { label: 'Developer', color: '#ff8800' };
    case 'influencer':
      return { label: 'Influencer', color: '#ff00ff' };
    case 'custom':
      return { label: 'Custom', color: '#888' };
    default:
      return { label: 'Unknown', color: '#888' };
  }
}

/**
 * Shorten wallet address for display
 */
export function shortenWalletAddress(address: string, chars: number = 4): string {
  if (address.length <= chars * 2 + 3) return address;
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Format USD amount
 */
export function formatUsd(amount: number): string {
  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `$${(amount / 1000).toFixed(2)}K`;
  }
  return `$${amount.toFixed(2)}`;
}

/**
 * Format time ago
 */
export function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
